import type { DemoEnvironmentResponse } from '../../services/api/types.gen';
import type { AgencyRailRow } from './AgencyRail';

/**
 * One agency's row in the Demo Sandboxes rail, and the search over them.
 *
 * The same shape Agency Data Fill's rows have, carrying demo facts instead of
 * Kinnser ones: what the sandbox's state is, when it was last built, and when
 * anybody last signed into it.
 *
 * Pure functions over what the list route returns, kept apart from the rail
 * and the pane so the rules below are testable without rendering.
 */

export interface DemoRailRow extends AgencyRailRow {
  /** The environment this row was built from, for the pane. */
  environment: DemoEnvironmentResponse;
}

/**
 * Sorted by the name the rows actually show.
 *
 * The route orders by ``system_name``, which is often not what a row displays:
 * AandA's written name is "A & A Home Health". Left alone the rail reads as
 * unordered, and scanning for an agency by eye stops working.
 */
export function demoRailRows(
  environments: readonly DemoEnvironmentResponse[]
): DemoRailRow[] {
  return environments
    .map(environment => ({
      agencyId: environment.agency_id,
      displayName:
        environment.agency_written_name || environment.agency_system_name,
      writtenName: environment.agency_written_name || null,
      systemName: environment.agency_system_name,
      environment,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/** Whether a row matches what was typed in the rail's search box. */
export function matchesSearch(row: DemoRailRow, search: string): boolean {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return (
    row.displayName.toLowerCase().includes(query) ||
    row.systemName.toLowerCase().includes(query)
  );
}

/**
 * When this sandbox was last worked on: pulled, built, or a run in flight.
 *
 * The demo screen's answer to the same question Agency Data Fill asks of its
 * own rows, so both rails share one window rule and differ only in what counts
 * as working on an agency.
 */
export function demoLastWorkedOn(row: DemoRailRow): string | null {
  const moments = [
    row.environment.kinnser_pull?.pulled_at ?? null,
    row.environment.setup_at ?? null,
    row.environment.latest_run?.finished_at ?? null,
    row.environment.latest_run?.started_at ?? null,
  ].filter((moment): moment is string => Boolean(moment));
  if (moments.length === 0) return null;
  return moments.reduce((latest, moment) =>
    moment > latest ? moment : latest
  );
}
